import {
  forwardRef,
  NamedExoticComponent,
  ReactElement,
  Ref,
  UIEventHandler,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as _ from 'lodash-es';
import {
  FormControl,
  InputLabel,
  SelectProps,
  FormHelperText,
  Typography,
  ClickAwayListener,
  Paper,
  Popper,
  Backdrop,
  Grow,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ZINDEX } from '../../constants';
import { Option } from '../../types';
import * as S from './styles';

export type MultiSelectProps = Omit<
  SelectProps,
  'onChange' | 'defaultValue'
> & {
  error?: boolean;
  disabled?: boolean;
  helperText?: string;
  label?: string;
  onChange: (values: Option[]) => void;
  options: Option[];
  placeholder?: string;
  value: string[];
  width?: number | string;
  height?: number | string;
  maxHeight?: number | string;
  onScroll?: UIEventHandler<HTMLDivElement>;
  onOpen?: (open: boolean) => void;
};

/**
 * 複数選択セレクトボックス
 * ※選択項目によってフィールドが伸び縮みしないように親コンポネントの横幅あるいはwidthを設定して横幅を固定してください。
 * @param error エラー有無
 * @param disabled 入力が無効か
 * @param helperText 下部に表示されるテキスト、エラーメッセージの表示等に利用
 * @param label セレクトボックスのラベル
 * @param onChange オプションの選択時に呼び出されるコールバック関数
 * @param options オプションの配列
 * @param placeholder 入力がないときに表示されるPlaceholder。placeholderが存在する場合は、値に""(空白文字)が割り当てられる。""を値として利用したい場合は、placeholderを設定しないこと。
 * @param value フィールドの値の配列
 * @param width フィールドの幅
 * @param height フィールドの高
 * @param maxHeight セレクトメニューの高さ デフォルト: 200
 * @param onOpen セレクトボックスを開く
 * @param onScroll セレクトボックスがスクロールされた時にデータを読み込む
 *
 * 上記のほかにMuiのSelectのPropsも渡すことができる
 * https://mui.com/material-ui/api/select/
 */
function BaseMultiSelect(
  {
    error,
    disabled,
    helperText,
    label,
    options,
    placeholder,
    value,
    width,
    maxHeight,
    onChange,
    onScroll,
    onOpen,
    ...props
  }: MultiSelectProps,
  ref: Ref<HTMLDivElement>
) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const selectedOptions = useMemo(
    () => _.filter(options, (option) => _.includes(value, option.value)),
    [options, value]
  );

  const handleOpenSelectBox = useCallback(
    (bool: boolean) => {
      setOpen(bool);
      if (!onOpen || !bool) return;
      onOpen(bool);
    },
    [onOpen]
  );

  const handleClick = useCallback(() => {
    if (disabled) return;
    handleOpenSelectBox(!open);
    if (anchorRef.current) {
      (anchorRef.current as HTMLElement).blur();
    }
  }, [open, disabled, handleOpenSelectBox]);

  const handleItemClick = useCallback(
    (option: Option) => {
      if (_.some(selectedOptions, option)) {
        onChange(_.reject(selectedOptions, { value: option.value }));
      } else {
        onChange([...selectedOptions, option]);
      }
    },
    [onChange, selectedOptions]
  );

  return (
    <FormControl
      disabled={disabled}
      error={error}
      sx={{ width: width || '100%' }}
    >
      <InputLabel
        shrink
        sx={(theme) => ({
          color: open ? theme.palette.primary.main : theme.palette.text.primary,
        })}
      >
        {label}
      </InputLabel>
      <S.StyledSelect
        autocompleteOpen={open}
        notched
        displayEmpty
        label={label}
        width={width}
        IconComponent={open ? ExpandLessIcon : ExpandMoreIcon}
        multiple
        ref={anchorRef}
        value={value}
        renderValue={() =>
          // 選択値に紐づくオプションが空ではない場合
          !_.isEmpty(selectedOptions) ? (
            <S.ChipsWrapper>
              {_.map(selectedOptions, (option, index) => (
                <Typography component="span" key={option.value}>
                  {index > 0 ? ', ' : ''}
                  {option.label}
                </Typography>
              ))}
            </S.ChipsWrapper>
          ) : (
            <S.Placeholder>{placeholder ?? ''}</S.Placeholder>
          )
        }
        onClick={handleClick}
        {...props}
        open={false}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}

      {/* ドロップダウンを表示中に縦スクロールを移動してもドロップダウンが移動されないように、ブラウザのMUIのSelectコンポーネントをそのまま使わずに、Backdrop、PopperとMenuItemを利用する */}
      <Backdrop
        open={open}
        sx={{ background: 'transparent', zIndex: ZINDEX.POPUP_WINDOW }}
        data-testid="backdrop"
      >
        <ClickAwayListener
          onClickAway={(event) => {
            const target = event.target as HTMLDivElement;
            if (anchorRef.current?.contains(target)) {
              return;
            }
            handleOpenSelectBox(false);
          }}
        >
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            sx={{ zIndex: ZINDEX.POPUP_WINDOW }}
          >
            {({ TransitionProps }) => (
              <Grow {...TransitionProps} in={open}>
                <Paper
                  sx={(theme) => ({
                    maxHeight: maxHeight || 260,
                    maxWidth: 600,
                    border: `1px solid ${theme.palette.border.main}`,
                    overflowY: 'auto',
                    padding: '8px 0',
                  })}
                  onScroll={onScroll}
                  data-testid="scrollContainer"
                >
                  {_.map(options, (option) => (
                    <S.StyledMenuItem
                      key={option.value}
                      value={option.value}
                      onClick={() => handleItemClick(option)}
                      selected={_.includes(value, option.value)}
                    >
                      <S.StyledCheckbox
                        checked={_.includes(value, option.value)}
                        readOnly
                      />
                      {option.label}
                    </S.StyledMenuItem>
                  ))}
                </Paper>
              </Grow>
            )}
          </Popper>
        </ClickAwayListener>
      </Backdrop>
    </FormControl>
  );
}

export const MultiSelect = forwardRef(BaseMultiSelect) as (
  p: MultiSelectProps & { ref?: Ref<HTMLDivElement> }
) => ReactElement;

(MultiSelect as NamedExoticComponent).displayName = 'MultiSelect';
